import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { waitForJob } from '@/api/jobs';
import type { Website } from '@/api/types';
import { Button, ConfirmHeader, KeyValueList, Modal, ModalFooter, useModalTitleId, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { refreshAfterDeploy, rollback, type DeploymentOut } from './api';

/**
 * «Откатить на vN» (DESIGN_SPEC §4.12): current, new active, «Кеш CDN: revalidate HTML». The API repoints
 * the active build and invalidates the caches; no rebuild (P5-B02).
 */
export function RollbackModal({ site, current, target, onClose }: { site: Website; current: DeploymentOut | null; target: DeploymentOut | null; onClose: () => void }) {
  const { t } = useTranslation('deploy');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const confirm = () => {
    if (!target || busy) return;
    setBusy(true);
    rollback(target.id)
      .then((r) => {
        onClose();
        refreshAfterDeploy(qc, site.id);
        // The job repoints in one transaction; the design's toast once it is live again.
        return waitForJob(r.job_id).then(() => {
          toast(t('rollback.done', { version: r.deployment.version }));
          refreshAfterDeploy(qc, site.id);
        });
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() => setBusy(false));
  };
  return (
    <Modal open={!!target} onClose={onClose} labelledBy={titleId}>
      {target && (
        <>
          <ConfirmHeader id={titleId} title={t('rollback.title', { version: target.version })} text={t('rollback.text')} />
          <KeyValueList
            rows={[
              { key: 'current', label: t('rollback.current'), value: current ? `v${current.version}` : tc('dash') },
              { key: 'next', label: t('rollback.next'), value: `v${target.version}` },
              { key: 'cdn', label: t('rollback.cdn'), value: t('rollback.cdn_value') },
            ]}
          />
          <ModalFooter>
            <Button variant="secondary" size={46} onClick={onClose}>
              {tc('actions.cancel')}
            </Button>
            <Button size={46} aria-busy={busy} onClick={confirm}>
              {t('rollback.submit')}
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
